import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Users, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    custom_audio_url: ''
  });
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('Acceso denegado: Solo administradores');
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users`,
        newUser,
        { headers: getAuthHeaders() }
      );
      toast.success('Usuario creado exitosamente');
      setNewUser({ name: '', email: '', username: '', password: '', custom_audio_url: '' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleEditUser = (userData) => {
    setEditingUser(userData);
    setAudioUrl(userData.custom_audio_url || '');
  };

  const handleSaveAudio = async () => {
    if (!editingUser) return;

    try {
      await axios.put(
        `${API}/admin/users/${editingUser.id}`,
        { custom_audio_url: audioUrl || null },
        { headers: getAuthHeaders() }
      );
      toast.success('Audio personalizado actualizado');
      setEditingUser(null);
      setAudioUrl('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar audio');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${userName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setAudioUrl('');
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8" data-testid="admin-panel">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Volver
        </Button>
      </div>

      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Users size={48} className="text-[#77C9D4]" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Panel de Administración</h1>
        <p className="text-gray-600">Gestionar usuarios y audios personalizados</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Create User Section */}
          <div className="glass rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Crear Nuevo Usuario</h2>
              <Button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full"
                data-testid="toggle-create-form"
              >
                <UserPlus size={18} className="mr-2" />
                {showCreateForm ? 'Cancelar' : 'Nuevo Usuario'}
              </Button>
            </div>

            {showCreateForm && (
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4 border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="new-name">Nombre *</Label>
                    <Input
                      id="new-name"
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      required
                      className="mt-1"
                      placeholder="Juan Pérez"
                      data-testid="new-user-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new-username">Nombre de Usuario *</Label>
                    <Input
                      id="new-username"
                      type="text"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      required
                      className="mt-1"
                      placeholder="usuario123"
                      data-testid="new-user-username"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="new-email">Email *</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      required
                      className="mt-1"
                      placeholder="usuario@email.com"
                      data-testid="new-user-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new-password">Contraseña *</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                      className="mt-1"
                      placeholder="Mínimo 6 caracteres"
                      data-testid="new-user-password"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-audio">Audio Personalizado (URL)</Label>
                  <Input
                    id="new-audio"
                    type="url"
                    value={newUser.custom_audio_url}
                    onChange={(e) => setNewUser({...newUser, custom_audio_url: e.target.value})}
                    className="mt-1"
                    placeholder="https://www.dropbox.com/... (recomendado) o URL directa"
                    data-testid="new-user-audio"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    💡 <strong>Recomendado - Dropbox:</strong><br/>
                    1. Sube el MP3 a Dropbox<br/>
                    2. Click derecho → Compartir → "Crear enlace"<br/>
                    3. Copiar el enlace y pegarlo aquí<br/>
                    4. El sistema lo convertirá automáticamente a enlace directo<br/>
                    <br/>
                    ⚠️ <strong>Google Drive:</strong> Puede tener problemas de CORS. Si usas Drive y no funciona, cambia a Dropbox.<br/>
                    <br/>
                    Este audio se reproducirá en bucle durante las apneas del usuario
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full py-6"
                  data-testid="create-user-button"
                >
                  <UserPlus size={18} className="mr-2" />
                  Crear Usuario
                </Button>
              </form>
            )}
          </div>

          {/* Users List */}
          <div className="glass rounded-2xl p-4 sm:p-6">
            <h2 className="text-xl font-bold mb-4">Usuarios Registrados ({users.length})</h2>
            <div className="space-y-4">
              {users.map((userData) => (
                <div
                  key={userData.id}
                  className="bg-white/50 rounded-xl p-4 border border-gray-200"
                  data-testid={`user-item-${userData.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{userData.name}</h3>
                      <p className="text-sm text-gray-600">{userData.email}</p>
                      {userData.custom_audio_url && (
                        <p className="text-xs text-[#77C9D4] mt-1 break-all">
                          🎵 Audio: {userData.custom_audio_url.substring(0, 50)}...
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditUser(userData)}
                        className="bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full"
                        data-testid={`edit-user-${userData.id}`}
                      >
                        {userData.custom_audio_url ? 'Editar Audio' : 'Asignar Audio'}
                      </Button>
                      <Button
                        onClick={() => handleDeleteUser(userData.id, userData.name)}
                        variant="destructive"
                        className="rounded-full"
                        data-testid={`delete-user-${userData.id}`}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>

                  {editingUser && editingUser.id === userData.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Label htmlFor="audio-url">URL del Audio Personalizado</Label>
                      <Input
                        id="audio-url"
                        type="url"
                        value={audioUrl}
                        onChange={(e) => setAudioUrl(e.target.value)}
                        placeholder="https://www.dropbox.com/... (recomendado) o URL directa"
                        className="mt-2"
                        data-testid="audio-url-input"
                      />
                      <p className="text-xs text-gray-600 mt-2">
                        💡 <strong>Recomendado - Dropbox:</strong><br/>
                        1. Sube el MP3 a Dropbox<br/>
                        2. Click derecho → Compartir → "Crear enlace"<br/>
                        3. Copiar el enlace y pegarlo aquí<br/>
                        4. El sistema lo convertirá automáticamente<br/>
                        <br/>
                        ⚠️ <strong>Google Drive:</strong> Puede no funcionar por CORS.<br/>
                        <br/>
                        Este audio se reproducirá en bucle durante las apneas del usuario
                      </p>
                      <div className="flex gap-2 mt-4">
                        <Button
                          onClick={handleSaveAudio}
                          className="bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full flex-1"
                          data-testid="save-audio-button"
                        >
                          <Save size={18} className="mr-2" />
                          Guardar
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          variant="outline"
                          className="rounded-full flex-1"
                          data-testid="cancel-edit-button"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
